import { useCentralizedMe } from '@crehana/auth';

import type { TRequestDetail } from '../../types';
import { useTimeAndAttendanceUserOvertimeRequestDetailQuery } from './get-overtime-request-detail.v5.generated';

type TUseRequestDetailResult = {
  detail: TRequestDetail | null;
  kind: 'loading' | 'ready' | 'forbidden' | 'error';
  refetch: () => void;
};

// The gateway answers HTTP 200 with the error member inside `data`, so Apollo never sets
// `error` for a Forbidden response. Branch on `__typename`, never on `error` alone.
export const useRequestDetail = (
  requestId: string | null,
): TUseRequestDetailResult => {
  const { currentPageOrganization } = useCentralizedMe();
  const orgId = currentPageOrganization?.id ?? 0;

  const { data, loading, error, refetch } =
    useTimeAndAttendanceUserOvertimeRequestDetailQuery({
      variables: {
        input: {
          centralized_organization_id: orgId,
          overtime_request_id: requestId ?? '',
        },
      },
      skip: !requestId || !orgId,
      context: { clientName: 'v5' },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    });

  const refetchVoid = () => {
    refetch().catch(() => undefined);
  };

  const result = data?.time.overtime_requests.get_overtime_request_detail;

  if (result?.__typename === 'TimeGetOvertimeRequestDetailPayload') {
    return {
      detail: result.overtime_request,
      kind: 'ready',
      refetch: refetchVoid,
    };
  }
  if (!requestId || loading) {
    return { detail: null, kind: 'loading', refetch: refetchVoid };
  }
  // A malformed id (OVERTIME_REQUEST_INVALID_ID) cannot be fixed by a retry.
  if (
    result?.__typename === 'ForbiddenErrorResponse' ||
    result?.__typename === 'ValidationErrorResponse'
  ) {
    return { detail: null, kind: 'forbidden', refetch: refetchVoid };
  }
  if (result || error) {
    return { detail: null, kind: 'error', refetch: refetchVoid };
  }
  return { detail: null, kind: 'loading', refetch: refetchVoid };
};
